import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Avatar, IconButton, Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  TableChart as TimesheetIcon,
  Assessment as ReportIcon,
  History as ActivityIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminsIcon,
  Work as ProjectsIcon,
  Receipt as ExpensesIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 240;

const adminMenu = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { label: 'Employees', icon: <PeopleIcon />, path: '/admin/employees' },
  { label: 'Admins', icon: <AdminsIcon />, path: '/admin/admins', masterOnly: true },
  { label: 'Clients', icon: <BusinessIcon />, path: '/admin/clients' },
  { label: 'Timesheets', icon: <TimesheetIcon />, path: '/admin/timesheets' },
  { label: 'Projects', icon: <ProjectsIcon />, path: '/admin/projects' },
  { label: 'Reports', icon: <ReportIcon />, path: '/admin/reports' },
  { label: 'Expenses', icon: <ExpensesIcon />, path: '/admin/expenses' },
  { label: 'Activity Logs', icon: <ActivityIcon />, path: '/admin/activity-logs' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
];

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const isCustomer = user?.role === 'customer';
  const isMasterAdmin = isAdmin && user?.username === 'admin';
  const customerClientName = isCustomer && user?.profile && 'client_name' in (user.profile as any)
    ? (user.profile as any).client_name as string
    : null;
  const customerLogoUrl = isCustomer && user?.profile && 'logo_url' in (user.profile as any)
    ? (user.profile as any).logo_url as string | null
    : null;

  const displayName = user?.profile && 'name' in user.profile ? user.profile.name : user?.username || '';

  const visibleMenu = adminMenu.filter(item => !item.masterOnly || isMasterAdmin);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {isAdmin && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              mt: '56px',
              borderRight: '1px solid #E2E8F0',
              backgroundColor: '#FFFFFF',
            },
          }}
        >
          <List sx={{ px: 1, pt: 1.5 }}>
            {visibleMenu.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItemButton
                  key={item.path}
                  selected={isActive}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.3,
                    mx: 0.5,
                    py: 0.9,
                    px: 1.5,
                    transition: 'all 0.15s ease',
                    borderLeft: isActive ? '3px solid #3B82F6' : '3px solid transparent',
                    '&.Mui-selected': {
                      backgroundColor: '#EFF6FF',
                      color: '#3B82F6',
                      '&:hover': { backgroundColor: '#DBEAFE' },
                      '& .MuiListItemIcon-root': { color: '#3B82F6' },
                    },
                    '&:hover': {
                      backgroundColor: '#F1F5F9',
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: 36,
                    color: isActive ? '#3B82F6' : '#64748B',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 13.5,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#3B82F6' : '#334155',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar variant="dense" sx={{ minHeight: 56 }}>
            {/* Left: brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {customerLogoUrl ? (
                <Box component="img" src={customerLogoUrl} alt={customerClientName || 'Client'}
                  sx={{ height: 32, maxWidth: 100, objectFit: 'contain' }} />
              ) : (
                <Box component="img" src="/logo192.png" alt="Kovil"
                  sx={{ width: 28, height: 28, borderRadius: '6px' }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#0F172A', letterSpacing: '-0.01em' }}>
                {customerClientName
                  ? <>{customerClientName} <Box component="span" sx={{ fontWeight: 400, color: '#94A3B8', fontSize: 15 }}>Timesheets</Box></>
                  : <>Kovil <Box component="span" sx={{ fontWeight: 500, color: '#64748B' }}>Timesheet</Box></>
                }
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Right: User avatar + name + logout */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{
                width: 32,
                height: 32,
                bgcolor: '#EFF6FF',
                color: '#3B82F6',
                fontSize: 13,
                fontWeight: 600,
              }}>
                {getInitials(displayName)}
              </Avatar>
              <Typography sx={{ fontWeight: 500, fontSize: 13.5, color: '#334155' }}>
                {displayName}
              </Typography>
              <Tooltip title="Logout" arrow>
                <IconButton
                  onClick={handleLogout}
                  size="small"
                  sx={{
                    color: '#94A3B8',
                    ml: 0.5,
                    '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, p: 2.5, mt: '56px', backgroundColor: '#F8FAFC' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
